// Parity P1 wave 1: the task layout editor (hybrid design §3.3, D6): sections on the left with
// their columns (steppers, + column, remove: content moves left, move to another section by
// dragging the column's header or its menu), a schematic preview of a sample task in the middle,
// the shelf of unplaced blocks and fields on the right; an explicit scope ("This board" /
// "Space default", the latter listing the boards it changes), the five latest versions, "Reset
// to space default". Every drag or click edits a draft: the preview moves, nothing is written
// until Save (P1 acceptance). Every drag has a twin (design principle 4): Alt+↑/↓ on an item,
// "+" on a shelf block, the column's "Move to" menu. Field rules (required stage, always show)
// save at once, with their own event.
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Segmented,
  Switch,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeOffIcon,
  FolderPlusIcon,
  GripVerticalIcon,
  HistoryIcon,
  PlusIcon,
  XIcon,
} from "lucide-react";
import { type DragEvent, useMemo, useState } from "react";
import type { Api } from "../../../api/client";
import type { CompanyAccess } from "../../company";
import type { SeatPeople } from "../model";
import {
  addColumn,
  addGroup,
  addSection,
  blockTitle,
  DEFAULT_LAYOUT,
  itemBlocks,
  type LayoutItem,
  moveColumn,
  moveSection,
  nudgeItem,
  placeBlock,
  removeBlock,
  removeColumn,
  removeSection,
  resizeColumn,
  SECTION_PRESETS,
  sameLayout,
  shelfOf,
  type TaskLayout,
  ungroup,
  updateSection,
} from "../task-layout/layout";
import { taskBlocks } from "../task-layout/registry";
import type { Board, FieldDef, TaskDetail } from "../view-engine";
import {
  type BoardTaskLayout,
  boardLayoutHistoryQuery,
  type FieldRule,
  p1Keys,
  spaceLayoutHistoryQuery,
  spaceLayoutQuery,
} from "./queries";

type Scope = "board" | "space";
/** Static classes (Tailwind sees them): a preview column's span of 12. */
const SPAN: Record<number, string> = {
  2: "col-span-2",
  3: "col-span-3",
  4: "col-span-4",
  5: "col-span-5",
  6: "col-span-6",
  7: "col-span-7",
  8: "col-span-8",
  9: "col-span-9",
  10: "col-span-10",
  11: "col-span-11",
  12: "col-span-12",
};
const BLOCK = "application/x-cos-block";
const COLUMN = "application/x-cos-column";

export function TaskLayoutEditor({
  api,
  company,
  board,
  spaceName,
  state,
  fields,
  sample,
  people,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  board: Board;
  spaceName: string;
  state: BoardTaskLayout;
  fields: FieldDef[];
  sample: TaskDetail;
  people: SeatPeople;
  onClose: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const space = useQuery(spaceLayoutQuery(api, co, board.space_id));
  const [scope, setScope] = useState<Scope>("board");
  const [boardDraft, setBoardDraft] = useState<TaskLayout>(state.effective);
  // Each draft keeps the version of the document it was made from (`base_version`): a save after
  // someone else's (seen through a refetch) is the server's conflict, not a silent overwrite.
  // The board draft starts from the layout the editor opened with; the space draft from the
  // space default shown when it is first edited (until then it follows refetches).
  const [boardFrom] = useState(state.version);
  const [spaceDraft, setSpaceDraft] = useState<{ layout: TaskLayout; from: number } | null>(null);
  const spaceBase = space.data?.effective ?? DEFAULT_LAYOUT;
  const spaceFrom = spaceDraft?.from ?? space.data?.version ?? 0;
  const draft = scope === "board" ? boardDraft : (spaceDraft?.layout ?? spaceBase);
  const setDraft = (next: TaskLayout) =>
    scope === "board"
      ? setBoardDraft(next)
      : setSpaceDraft((d) => ({ layout: next, from: d?.from ?? space.data?.version ?? 0 }));
  const saved = scope === "board" ? state.effective : spaceBase;
  const dirty = !sameLayout(draft, saved);
  const canSpace = space.data?.can_edit ?? false;

  const titles = useMemo(() => new Map(taskBlocks().map((b) => [b.id, b.title])), []);
  const fieldName = (id: string) => fields.find((f) => f.id === id)?.name;
  const title = (id: string) => blockTitle(id, titles, fieldName);
  const known = [
    ...taskBlocks()
      .filter((b) => b.placement === "body")
      .map((b) => b.id),
    // A field belongs to one board: a space default places only "Other fields".
    ...(scope === "board" ? fields.map((f) => `field:${f.id}`) : []),
    "fields:rest",
  ];
  const shelf = shelfOf(draft, known);

  const onSaved = () => {
    void qc.invalidateQueries({ queryKey: p1Keys.boardLayout(co, board.id) });
    void qc.invalidateQueries({ queryKey: p1Keys.spaceLayout(co, board.space_id) });
    void qc.invalidateQueries({ queryKey: ["company", co, "work", "board"] });
  };
  const save = useMutation({
    mutationFn: async (layout: TaskLayout | null) => {
      if (scope === "board")
        await api.client.taskLayout.board.put({
          params: { co, id: board.id },
          body: { layout, base_version: boardFrom },
        });
      else
        await api.client.taskLayout.space.put({
          params: { co, id: board.space_id },
          body: { layout, base_version: spaceFrom },
        });
    },
    onError: (e) => {
      toast.add({
        title:
          e instanceof ApiError && e.status === 409
            ? "Someone saved this layout while you edited it. Close and reopen to start from theirs."
            : "The layout could not be saved.",
        type: "error",
      });
    },
    onSuccess: (_r, layout) => {
      toast.add({
        title:
          layout === null
            ? "Reset to the space default"
            : scope === "board"
              ? `Layout saved for ${board.name}`
              : `Space default saved for ${spaceName}`,
        type: "success",
      });
      onSaved();
      onClose();
    },
  });
  const affected = (space.data?.boards ?? []).filter((b) => b.uses_default);
  // A version restored through the API (its own event, `restored_from`, a reset stays a reset).
  const restore = useMutation({
    mutationFn: async (version: number) => {
      if (scope === "board")
        await api.client.taskLayout.board.restore({
          params: { co, id: board.id },
          body: { version, base_version: boardFrom },
        });
      else
        await api.client.taskLayout.space.restore({
          params: { co, id: board.space_id },
          body: { version, base_version: spaceFrom },
        });
    },
    onError: (e) => {
      toast.add({
        title:
          e instanceof ApiError && e.status === 409
            ? "Someone saved this layout while you edited it. Close and reopen to start from theirs."
            : "The version could not be restored.",
        type: "error",
      });
    },
    onSuccess: (_r, version) => {
      toast.add({ title: `Version ${version} restored`, type: "success" });
      onSaved();
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent
        data-slot="layout-editor"
        className="flex h-[min(820px,calc(100dvh-48px))] w-[min(1240px,calc(100vw-32px))] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none"
      >
        <div className="flex flex-wrap items-center gap-3 border-b border-subtle px-6 py-3 pr-14">
          <DialogTitle className="text-base font-semibold">Task layout — {board.name}</DialogTitle>
          <DialogDescription className="sr-only">
            Arrange the sections, columns and blocks every task of this board shows.
          </DialogDescription>
          <span className="ml-auto text-xs text-muted-foreground">Scope</span>
          <Segmented<Scope>
            aria-label="Scope"
            size="sm"
            value={scope}
            onValueChange={(s) => {
              if (s === "space" && !canSpace) return;
              setScope(s);
            }}
            options={[
              { value: "board", label: "This board" },
              {
                value: "space",
                label: canSpace ? "Space default" : "Space default (space managers)",
              },
            ]}
          />
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-[minmax(300px,360px)_minmax(0,1fr)_220px] max-lg:grid-cols-[minmax(280px,1fr)_220px]">
          <SectionsPane draft={draft} setDraft={setDraft} title={title} />
          <Preview draft={draft} title={title} sample={sample} setDraft={setDraft} />
          <aside
            aria-label="Shelf"
            data-slot="layout-shelf"
            className="min-h-0 overflow-y-auto border-l border-subtle bg-sunken px-3 py-3"
          >
            <p className="mb-1 text-xs font-medium text-muted-foreground">Not placed</p>
            {shelf.length ? (
              <ul className="m-0 flex list-none flex-col gap-1 p-0">
                {shelf.map((id) => (
                  <li key={id}>
                    <BlockChip
                      id={id}
                      label={title(id)}
                      onAdd={() => {
                        const s = Math.max(0, draft.sections.length - 1);
                        setDraft(placeBlock(draft, id, { section: s, column: 0 }));
                      }}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">Everything is placed.</p>
            )}
            <p className="mt-4 text-xs text-muted-foreground">
              Fixed for every task: title, tags, status, assignee, delegate, dates, priority.
            </p>
            <FieldRules api={api} co={co} boardId={board.id} fields={fields} rules={state.fields} />
          </aside>
        </div>
        {scope === "space" ? (
          <Notice tone="info" className="mx-6 mt-3" data-slot="layout-affected">
            Saving the space default changes {affected.length}{" "}
            {affected.length === 1 ? "board" : "boards"} without a layout of their own
            {affected.length ? `: ${affected.map((b) => b.name).join(", ")}` : ""}.
          </Notice>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 border-t border-subtle px-6 py-3">
          <Versions
            api={api}
            co={co}
            scope={scope}
            boardId={board.id}
            spaceId={board.space_id}
            people={people}
            onPick={(v) => restore.mutate(v)}
          />
          {scope === "board" && state.source === "board" ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => save.mutate(null)}
              disabled={save.isPending}
            >
              Reset to space default
            </Button>
          ) : null}
          {dirty ? <Badge variant="neutral">Unsaved changes</Badge> : null}
          <Button variant="ghost" size="sm" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            data-slot="layout-save"
            disabled={!dirty || save.isPending || (scope === "space" && !canSpace)}
            onClick={() => save.mutate(draft)}
          >
            {scope === "board" ? "Save for this board" : "Save space default"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BlockChip({ id, label, onAdd }: { id: string; label: string; onAdd?: () => void }) {
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: a pointer drag; its twin is the "Place" button.
    <span
      draggable
      data-shelf-block={id}
      onDragStart={(e) => {
        e.dataTransfer.setData(BLOCK, id);
        e.dataTransfer.effectAllowed = "move";
      }}
      className="flex h-8 cursor-grab items-center gap-1.5 rounded-md border border-border bg-card px-2 text-sm"
    >
      <GripVerticalIcon aria-hidden="true" className="size-3.5 text-faint" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {onAdd ? (
        <Button variant="ghost" size="icon-xs" aria-label={`Place ${label}`} onClick={onAdd}>
          <PlusIcon aria-hidden="true" />
        </Button>
      ) : null}
    </span>
  );
}

/** Drop handlers for a block or column dragged onto a target. */
function dropZone(
  onBlock: (id: string) => void,
  onColumn?: (from: { section: number; column: number }) => void,
) {
  return {
    onDragOver: (e: DragEvent) => {
      const types = [...e.dataTransfer.types];
      if (types.includes(BLOCK) || (onColumn && types.includes(COLUMN))) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }
    },
    onDrop: (e: DragEvent) => {
      const block = e.dataTransfer.getData(BLOCK);
      const col = e.dataTransfer.getData(COLUMN);
      if (block) {
        e.preventDefault();
        e.stopPropagation();
        onBlock(block);
      } else if (col && onColumn) {
        e.preventDefault();
        e.stopPropagation();
        const [s, c] = col.split(":").map(Number);
        onColumn({ section: s ?? 0, column: c ?? 0 });
      }
    },
  };
}

function SectionsPane({
  draft,
  setDraft,
  title,
}: {
  draft: TaskLayout;
  setDraft: (l: TaskLayout) => void;
  title: (id: string) => string;
}) {
  const [preset, setPreset] = useState("wide");
  return (
    <section
      aria-label="Sections"
      data-slot="layout-sections"
      className="min-h-0 overflow-y-auto border-r border-subtle px-4 py-3"
    >
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        {draft.sections.map((s, si) => (
          <li
            key={s.id}
            data-layout-section={s.id}
            className="rounded-lg border border-border bg-card p-2"
            {...dropZone(
              (b) => setDraft(placeBlock(draft, b, { section: si, column: 0 })),
              (from) => setDraft(moveColumn(draft, from, si)),
            )}
          >
            <div className="flex items-center gap-1">
              <Input
                aria-label="Section title"
                className="h-7 flex-1 px-2 font-medium"
                value={s.title ?? ""}
                placeholder="Untitled section"
                onChange={(e) => setDraft(updateSection(draft, si, { title: e.target.value }))}
              />
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Move ${s.title ?? "section"} up`}
                disabled={si === 0}
                onClick={() => setDraft(moveSection(draft, si, -1))}
              >
                <ArrowUpIcon aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Move ${s.title ?? "section"} down`}
                disabled={si === draft.sections.length - 1}
                onClick={() => setDraft(moveSection(draft, si, 1))}
              >
                <ArrowDownIcon aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={
                  s.hidden ? `Show ${s.title ?? "section"}` : `Hide ${s.title ?? "section"}`
                }
                aria-pressed={!s.hidden}
                onClick={() =>
                  setDraft(updateSection(draft, si, { hidden: s.hidden ? undefined : true }))
                }
              >
                {s.hidden ? <EyeOffIcon aria-hidden="true" /> : <EyeIcon aria-hidden="true" />}
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Remove ${s.title ?? "section"}`}
                onClick={() => setDraft(removeSection(draft, si))}
              >
                <XIcon aria-hidden="true" />
              </Button>
            </div>
            <div className="mt-2 flex flex-col gap-2">
              {s.columns.map((c, ci) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: a column's position is its identity.
                  key={ci}
                  data-layout-column={`${si}:${ci}`}
                  className="rounded-md border border-dashed border-border px-2 py-1.5"
                  {...dropZone((b) => setDraft(placeBlock(draft, b, { section: si, column: ci })))}
                >
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: a pointer drag; its twin is "Move to…". */}
                  <div
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(COLUMN, `${si}:${ci}`);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    className="flex cursor-grab items-center gap-1 text-xs text-muted-foreground"
                  >
                    <GripVerticalIcon aria-hidden="true" className="size-3.5" />
                    <span className="tabular-nums">col {c.width}</span>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Narrower"
                      onClick={() => setDraft(resizeColumn(draft, si, ci, -1))}
                    >
                      <ChevronLeftIcon aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Wider"
                      onClick={() => setDraft(resizeColumn(draft, si, ci, 1))}
                    >
                      <ChevronRightIcon aria-hidden="true" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        render={<Button variant="ghost" size="xs" aria-label="Move this column" />}
                      >
                        Move to…
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuGroup>
                          <DropdownMenuLabel>Move the column to</DropdownMenuLabel>
                          {draft.sections.map((t, ti) =>
                            ti === si ? null : (
                              <DropdownMenuItem
                                key={t.id}
                                disabled={t.columns.length >= 4 || s.columns.length <= 1}
                                onClick={() =>
                                  setDraft(moveColumn(draft, { section: si, column: ci }, ti))
                                }
                              >
                                {t.title || "Untitled section"}
                              </DropdownMenuItem>
                            ),
                          )}
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Add a group"
                      onClick={() => setDraft(addGroup(draft, si, ci, "Group"))}
                    >
                      <FolderPlusIcon aria-hidden="true" />
                    </Button>
                    {s.columns.length > 1 ? (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        className="ml-auto"
                        aria-label="Remove the column (its blocks move left)"
                        onClick={() => setDraft(removeColumn(draft, si, ci))}
                      >
                        <XIcon aria-hidden="true" />
                      </Button>
                    ) : null}
                  </div>
                  <ul className="m-0 mt-1 flex list-none flex-col gap-1 p-0">
                    {c.items.map((it, ii) => (
                      <ItemRow
                        key={typeof it === "string" ? it : `g${ii}`}
                        item={it}
                        at={{ section: si, column: ci, index: ii }}
                        draft={draft}
                        setDraft={setDraft}
                        title={title}
                      />
                    ))}
                    {c.items.length === 0 ? (
                      <li className="text-xs text-muted-foreground">Drop blocks here</li>
                    ) : null}
                  </ul>
                </div>
              ))}
              {s.columns.length < 4 ? (
                <Button
                  variant="ghost"
                  size="xs"
                  className="w-fit"
                  onClick={() => setDraft(addColumn(draft, si))}
                >
                  <PlusIcon aria-hidden="true" />
                  Column
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-3 flex items-center gap-2">
        <NativeSelect
          size="sm"
          aria-label="New section's columns"
          value={preset}
          onChange={(e) => setPreset(e.target.value)}
        >
          {SECTION_PRESETS.map((p) => (
            <NativeSelectOption key={p.id} value={p.id}>
              {p.label}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <Button
          variant="outline"
          size="sm"
          disabled={draft.sections.length >= 20}
          onClick={() => setDraft(addSection(draft, preset, "New section"))}
        >
          <PlusIcon aria-hidden="true" />
          Add section
        </Button>
      </div>
    </section>
  );
}

function ItemRow({
  item,
  at,
  draft,
  setDraft,
  title,
}: {
  item: LayoutItem;
  at: { section: number; column: number; index: number };
  draft: TaskLayout;
  setDraft: (l: TaskLayout) => void;
  title: (id: string) => string;
}) {
  const keys = (e: React.KeyboardEvent) => {
    if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    e.preventDefault();
    setDraft(nudgeItem(draft, at, e.key === "ArrowUp" ? -1 : 1));
  };
  if (typeof item === "string")
    return (
      <li
        draggable
        data-layout-item={item}
        onDragStart={(e) => {
          e.dataTransfer.setData(BLOCK, item);
          e.dataTransfer.effectAllowed = "move";
        }}
        {...dropZone((b) => b !== item && setDraft(placeBlock(draft, b, { ...at })))}
        className="flex h-7 cursor-grab items-center gap-1.5 rounded-sm bg-muted/60 px-1.5 text-sm"
      >
        <button
          type="button"
          aria-label={`Move ${title(item)} (Alt+↑/↓)`}
          aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
          onKeyDown={keys}
          className="flex cursor-grab items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <GripVerticalIcon aria-hidden="true" className="size-3.5 text-faint" />
        </button>
        <span className="min-w-0 flex-1 truncate">{title(item)}</span>
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={`Take ${title(item)} off the layout`}
          onClick={() => setDraft(removeBlock(draft, item))}
        >
          <XIcon aria-hidden="true" />
        </Button>
      </li>
    );
  return (
    <li
      data-layout-group={item.group.title}
      className="rounded-md border border-border bg-sunken p-1.5"
      {...dropZone((b) => setDraft(placeBlock(draft, b, { ...at, intoGroup: at.index })))}
    >
      <div className="flex items-center gap-1">
        <Input
          aria-label="Group title"
          className="h-6 flex-1 px-1.5 text-xs"
          value={item.group.title}
          onChange={(e) => {
            const next = structuredClone(draft);
            const g = next.sections[at.section]?.columns[at.column]?.items[at.index];
            if (g && typeof g !== "string") g.group.title = e.target.value || "Group";
            setDraft(next);
          }}
        />
        <Button
          variant="ghost"
          size="xs"
          aria-label={`Ungroup ${item.group.title}`}
          onClick={() => setDraft(ungroup(draft, at))}
        >
          Ungroup
        </Button>
      </div>
      <ul className="m-0 mt-1 flex list-none flex-col gap-1 p-0">
        {itemBlocks(item).map((b) => (
          <li
            key={b}
            draggable
            data-layout-item={b}
            onDragStart={(e) => {
              e.dataTransfer.setData(BLOCK, b);
              e.dataTransfer.effectAllowed = "move";
            }}
            className="flex h-7 cursor-grab items-center gap-1.5 rounded-sm bg-card px-1.5 text-sm"
          >
            <GripVerticalIcon aria-hidden="true" className="size-3.5 text-faint" />
            <span className="min-w-0 flex-1 truncate">{title(b)}</span>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Take ${title(b)} off the layout`}
              onClick={() => setDraft(removeBlock(draft, b))}
            >
              <XIcon aria-hidden="true" />
            </Button>
          </li>
        ))}
        {item.group.blocks.length === 0 ? (
          <li className="text-xs text-muted-foreground">Drop blocks into the group</li>
        ) : null}
      </ul>
    </li>
  );
}

/** The schematic preview: the draft's sections and columns with the sample task's blocks. */
function Preview({
  draft,
  title,
  sample,
  setDraft,
}: {
  draft: TaskLayout;
  title: (id: string) => string;
  sample: TaskDetail;
  setDraft: (l: TaskLayout) => void;
}) {
  const box = (id: string) => (
    <div
      key={id}
      data-preview-block={id}
      className="rounded-md border border-border bg-card px-2 py-1.5 text-xs"
    >
      <span className="font-medium">{title(id)}</span>
    </div>
  );
  return (
    <section
      aria-label="Preview"
      data-slot="layout-preview"
      className="min-h-0 overflow-y-auto px-6 py-4 max-lg:hidden"
    >
      <p className="mb-2 text-xs text-muted-foreground">Preview · {sample.title}</p>
      <div className="flex flex-col gap-3">
        {draft.sections.map((s, si) => (
          <div
            key={s.id}
            data-preview-section={s.id}
            className={cn("rounded-lg border border-subtle p-3", s.hidden && "opacity-40")}
          >
            <p className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              {s.title || "Untitled section"}
              {s.hidden ? " (hidden)" : ""}
            </p>
            <div className="grid grid-cols-12 gap-3">
              {s.columns.map((c, ci) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: a column's position is its identity.
                  key={ci}
                  data-preview-column={`${si}:${ci}`}
                  className={cn(
                    "flex min-h-12 flex-col gap-1.5 rounded-md bg-sunken p-1.5",
                    SPAN[c.width] ?? "col-span-12",
                  )}
                  {...dropZone((b) => setDraft(placeBlock(draft, b, { section: si, column: ci })))}
                >
                  {c.items.map((it, ii) =>
                    typeof it === "string" ? (
                      box(it)
                    ) : (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: a group's position keys it.
                        key={`g${ii}`}
                        data-preview-group={it.group.title}
                        className="flex flex-col gap-1 rounded-md border border-border p-1.5"
                      >
                        <span className="text-xs font-medium text-muted-foreground">
                          ▣ {it.group.title}
                        </span>
                        {it.group.blocks.map(box)}
                      </div>
                    ),
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Versions({
  api,
  co,
  scope,
  boardId,
  spaceId,
  people,
  onPick,
}: {
  api: Api;
  co: string;
  scope: Scope;
  boardId: string;
  spaceId: string;
  people: SeatPeople;
  /** Restores that version (a reset stays a reset: the board inherits again). */
  onPick: (version: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const qb = useQuery({
    ...boardLayoutHistoryQuery(api, co, boardId),
    enabled: open && scope === "board",
  });
  const qs = useQuery({
    ...spaceLayoutHistoryQuery(api, co, spaceId),
    enabled: open && scope === "space",
  });
  const q = scope === "board" ? qb : qs;
  const rows = q.data?.data ?? [];
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger render={<Button variant="ghost" size="sm" />}>
        <HistoryIcon aria-hidden="true" />
        Versions
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-64">
        <DropdownMenuGroup>
          <DropdownMenuLabel>The latest five (pick one to restore it)</DropdownMenuLabel>
          {rows.length ? (
            rows.map((v) => (
              <DropdownMenuItem key={v.version} onClick={() => onPick(v.version)}>
                <span className="tabular-nums">v{v.version}</span>
                <span className="min-w-0 flex-1 truncate text-muted-foreground">
                  {v.layout ? "" : "reset · "}
                  {people.nameOf(v.saved_by.membership_id ?? null).name} ·{" "}
                  {new Date(v.saved_at).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </DropdownMenuItem>
            ))
          ) : (
            <DropdownMenuItem disabled>{q.isPending ? "Loading…" : "Never saved"}</DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const STAGES: { value: FieldRule["required_on"]; label: string }[] = [
  { value: "none", label: "Optional" },
  { value: "create", label: "Required on create" },
  { value: "complete", label: "Required to complete" },
  { value: "both", label: "Required on create and complete" },
];

/** Each field's requirement stage and "always show" (saved at once, per field). */
function FieldRules({
  api,
  co,
  boardId,
  fields,
  rules,
}: {
  api: Api;
  co: string;
  boardId: string;
  fields: FieldDef[];
  rules: FieldRule[];
}) {
  const qc = useQueryClient();
  const by = new Map(rules.map((r) => [r.field_def_id, r]));
  const set = useMutation({
    mutationFn: (v: {
      id: string;
      body: Partial<Pick<FieldRule, "required_on" | "always_show" | "show_on_card">>;
    }) => api.client.fields.rule({ params: { co, id: v.id }, body: v.body }),
    onSettled: () => void qc.invalidateQueries({ queryKey: p1Keys.boardLayout(co, boardId) }),
  });
  if (!fields.length) return null;
  return (
    <div data-slot="field-rules" className="mt-4">
      <p className="mb-1 text-xs font-medium text-muted-foreground">Field rules</p>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {fields
          .filter((f) => !["formula", "rollup"].includes(f.type))
          .map((f) => {
            const r = by.get(f.id);
            return (
              <li key={f.id} className="flex flex-col gap-1">
                <span className="truncate text-sm">{f.name}</span>
                <NativeSelect
                  size="sm"
                  aria-label={`${f.name}: required`}
                  value={r?.required_on ?? "none"}
                  onChange={(e) =>
                    set.mutate({
                      id: f.id,
                      body: { required_on: e.target.value as FieldRule["required_on"] },
                    })
                  }
                >
                  {STAGES.map((s) => (
                    <NativeSelectOption key={s.value} value={s.value}>
                      {s.label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Switch
                    size="sm"
                    aria-label={`${f.name}: always show`}
                    checked={r?.always_show ?? false}
                    onCheckedChange={(on) => set.mutate({ id: f.id, body: { always_show: on } })}
                  />
                  Always show
                </span>
                {/* T4-VIEWS-MEDIA: the field-wide card default (a view's own card fields win). */}
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Switch
                    size="sm"
                    aria-label={`${f.name}: on cards`}
                    checked={r?.show_on_card ?? false}
                    onCheckedChange={(on) => set.mutate({ id: f.id, body: { show_on_card: on } })}
                  />
                  On cards
                </span>
              </li>
            );
          })}
      </ul>
    </div>
  );
}
